import React from 'react';
import WallPreviewRenderer from './WallPreviewRenderer';
import WallVideoSplitPreview from './WallVideoSplitPreview';
import { isVideoUrl } from './WallItemPreview';

/**
 * Smart Template Engine — Preview System (Phase 3A).
 *
 * Renders ONE *RTDB* playlist item (already-resolved URLs + template block) across
 * the wall grid — the live counterpart of WallItemPreview. Templates render from
 * the `template` block the backend already emitted; full_wall uses the per-cell
 * `split_urls` (or crops `url`); split uses `screen_assignments` URLs. Read-only.
 */
function Cell({ url, w, h }) {
  return (
    <div style={{ width: w, height: h, overflow: 'hidden', borderRadius: 4, background: '#111' }}>
      {url ? (isVideoUrl(url)
        ? <video src={url} muted autoPlay loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />) : null}
    </div>
  );
}

export default function WallLiveItemPreview({ item, grid, maxWidth = 560 }) {
  if (!item) return null;
  const cols = Math.max(1, grid?.cols || 1);
  const rows = Math.max(1, grid?.rows || 1);
  const portrait = grid?.orientation === 'portrait';
  const cellAspect = portrait ? 9 / 16 : 16 / 9;
  const bezel = 4;
  const cellW = (maxWidth - (cols - 1) * bezel) / cols;
  const cellH = cellW / cellAspect;

  const gridWrap = (children) => (
    // Physical screen layout — pin LTR so RTL never mirrors the cells.
    <div dir="ltr" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, ${cellW}px)`,
      gridTemplateRows: `repeat(${rows}, ${cellH}px)`, gap: bezel, background: '#000', padding: bezel,
      borderRadius: 10, width: 'fit-content' }}>{children}</div>
  );

  if (item.content_type === 'template' || item.kind === 'template') {
    return <WallPreviewRenderer template={item.template} grid={grid} maxWidth={maxWidth} />;
  }

  const hasMap = (m) => m && typeof m === 'object' && Object.keys(m).length > 0;

  // full_wall with pre-split pieces → each cell plays its own piece full-screen.
  if (item.content_type === 'full_wall' && hasMap(item.split_urls)) {
    return gridWrap(Array.from({ length: rows * cols }).map((_, i) => (
      <Cell key={i} url={item.split_urls[String(i + 1)]} w={cellW} h={cellH} />
    )));
  }
  // full_wall without pieces → crop the source across the grid.
  if (item.content_type === 'full_wall' && item.url) {
    return <WallVideoSplitPreview src={item.url} grid={grid} maxWidth={maxWidth}
      fit={item.fit_mode || 'crop'} kind={isVideoUrl(item.url) ? 'video' : 'image'} />;
  }
  // split → per-cell assigned URL.
  if (item.content_type === 'split') {
    return gridWrap(Array.from({ length: rows * cols }).map((_, i) => (
      <Cell key={i} url={item.screen_assignments?.[String(i + 1)]} w={cellW} h={cellH} />
    )));
  }

  // media_file — mirrored: the SAME media plays on every screen. Render one cell
  // per screen (all identical) WITH bezel gaps, so the preview reads as "N screens
  // each showing the same thing" — not one big flat image (indistinguishable from a
  // single 1×1 wall).
  return gridWrap(Array.from({ length: rows * cols }).map((_, i) => (
    <Cell key={i} url={item.url} w={cellW} h={cellH} />
  )));
}
